import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { Link, useSearchParams } from "react-router-dom";

import LoginLayout from "components/layouts/login-layout/login-layout";
import SignUpForm from "components/sign-up-form/sign-up-form";
import GoogleAuthButton from "components/google-auth-button/google-auth-button";

import routeNames from "constants/route-names";
import { GoogleAuthStatus } from "constants/google-auth-status";
import { useSignUp } from "hooks/useSignUp";

const SignUpScreen = () => {
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const emailParam = searchParams.get("email");
  // Get the continue URL params to preserve them through verification flow
  const guideId = searchParams.get("guideId");

  // Use the shared sign-up hook
  const signUpHook = useSignUp({ guideId });

  // While Google is resolving a brand new account, the email/password form is hidden: it is
  // not a second way to finish that same flow.
  const [googleAuthStatus, setGoogleAuthStatus] = useState<GoogleAuthStatus>(GoogleAuthStatus.IDLE);
  const isGoogleAuthActive = googleAuthStatus !== GoogleAuthStatus.IDLE;

  // Set email from URL params if present
  useEffect(() => {
    if (emailParam) {
      signUpHook.setEmail(emailParam);
      form.setFieldsValue({ email: emailParam });
    }
  }, [emailParam, form]);

  return (
    <LoginLayout
      title="Let’s get started!"
      logoStyle={{ top: "8%" }}
      childrenLoading={signUpHook.loading}
      className="signup-screen"
    >
      <div className="sign-up-container">
        <GoogleAuthButton
          termsAccepted={signUpHook.termsAccepted}
          invitedEmail={emailParam}
          onStatusChange={setGoogleAuthStatus}
          showDivider={!isGoogleAuthActive}
        />

        {!isGoogleAuthActive && (
          <>
            <SignUpForm
              form={form}
              firstName={signUpHook.firstName}
              lastName={signUpHook.lastName}
              email={signUpHook.email}
              password={signUpHook.password}
              termsAccepted={signUpHook.termsAccepted}
              setFirstName={signUpHook.setFirstName}
              setLastName={signUpHook.setLastName}
              setEmail={signUpHook.setEmail}
              setPassword={signUpHook.setPassword}
              setTermsAccepted={signUpHook.setTermsAccepted}
              onSubmit={signUpHook.createUser}
              loading={signUpHook.loading}
              error={signUpHook.signUpError}
              emailParam={emailParam}
            />

            <span className="link-label sign-up-sign-in-prompt">
              Already have an account?
              <Link
                to={{
                  pathname: routeNames.LOGIN,
                }}
                className="inline-link"
                rel="noopener"
              >
                Sign in
              </Link>
            </span>
          </>
        )}
      </div>
    </LoginLayout>
  );
};

export default SignUpScreen;
